import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { AppTableAction, AuthPermissions } from '../../../_models/models';

export interface RowActionContext {
  row: any;
  authPermissions: AuthPermissions;
  isExternal: boolean;
}

export interface RowActionBehavior {
  icon: IconProp;
  isVisible: (action: AppTableAction, context: RowActionContext) => boolean;
  isDisabled?: (context: RowActionContext) => boolean;
  disabledTooltip?: (context: RowActionContext) => string;
  confirmOkText?: string;
  isDanger?: boolean;
}

export interface RowActionItem {
  action: AppTableAction;
  kind: 'confirm' | 'link' | 'click';
  label: string;
  icon: IconProp;
  isDisabled: boolean;
  tooltip: string;
  elementId: string;
  routerLink: string;
  confirmOkText: string;
  confirmDescription: string;
  isDanger: boolean;
  colorClass: string;
}

const isRowDeleted = (context: RowActionContext): boolean => !!(context.row?.deletedAt || context.row?.deleted_at);

const hasPermission = (action: AppTableAction, context: RowActionContext): boolean =>
  !!(context.authPermissions as Record<string, boolean>)?.[action.auth];

const isAllowedAndNotDeleted = (action: AppTableAction, context: RowActionContext): boolean =>
  hasPermission(action, context) && !isRowDeleted(context);

const isAllowedInternally = (action: AppTableAction, context: RowActionContext): boolean =>
  hasPermission(action, context) && !context.isExternal;

export const INLINE_ACTIONS_THRESHOLD = 1;

export const DEFAULT_ROW_ACTION_ICON: IconProp = 'circle';

export const ROW_ACTION_COLOR_CLASS = 'font-icon-color-grey-cool font-icon-hover-color-primary-black';
export const ROW_ACTION_DANGER_COLOR_CLASS = 'font-icon-color-warning-mid-dark';

export const DEFAULT_ROW_ACTION_BEHAVIOR: RowActionBehavior = {
  icon: DEFAULT_ROW_ACTION_ICON,
  isVisible: (action, context) => hasPermission(action, context) || context.isExternal
};

export const ROW_ACTION_BEHAVIORS: Record<string, RowActionBehavior> = {
  edit: {
    icon: 'pen',
    isVisible: hasPermission,
    isDisabled: context => !context.isExternal && isRowDeleted(context)
  },
  'edit-restore': {
    icon: 'pen',
    isVisible: isAllowedInternally
  },
  view: {
    icon: 'eye',
    isVisible: (action, context) => !context.authPermissions?.canUpdate && !context.isExternal,
    isDisabled: isRowDeleted
  },
  'view-deleted': {
    icon: 'eye',
    isVisible: (action, context) => hasPermission(action, context) && (context.isExternal || isRowDeleted(context))
  },
  restore: {
    icon: 'redo-alt',
    isVisible: isAllowedInternally
  },
  duplicate: {
    icon: 'copy',
    isVisible: hasPermission,
    isDisabled: context => (context.isExternal ? !context.row?.allowedToClone : !!context.row?.deleted_at),
    disabledTooltip: context =>
      context.isExternal ? 'Deal cannot be duplicated. Contact your Nexxen Account Manager.' : null
  },
  selectButton: {
    icon: 'plus-circle',
    isVisible: hasPermission
  },
  delete: {
    icon: 'trash-alt',
    isVisible: isAllowedAndNotDeleted,
    confirmOkText: 'Delete',
    isDanger: true
  },
  'new-delete': {
    icon: 'trash-alt',
    isVisible: isAllowedAndNotDeleted,
    isDanger: true
  },
  archive: {
    icon: 'box-open',
    isVisible: isAllowedAndNotDeleted,
    confirmOkText: 'Archive'
  }
};

export const resolveRowActionBehavior = (action: AppTableAction): RowActionBehavior =>
  ROW_ACTION_BEHAVIORS[action.name] || DEFAULT_ROW_ACTION_BEHAVIOR;
